import { useCallback, useEffect, useRef, useState } from 'react'
import { Icon } from './Icon'
import { Picker } from '@/components/Picker'
import { RubricOutline } from './RubricOutline'
import { useRubricDrag, useRubricFlip, type RubricDragItem, type RubricDropTarget } from './useRubricDrag'
import { sectionsFor } from '@/lib/rubricLayout'
import type { RubricCriterion, RubricLayout, RubricSection } from '@/lib/types'
import rubricUpdate from '@schemas/rubric_update.schema.json'

const MIN_CRITERIA = rubricUpdate.properties.rubric.minItems

export const blankCriterion = (n = 1): RubricCriterion => ({ criterion_id: `R${n}`, criterion: '', role: 'required' })

const fitText = (input: HTMLTextAreaElement | null) => {
  if (!input || !input.getClientRects().length) return
  input.style.height = '0px'
  input.style.height = `${input.scrollHeight + input.offsetHeight - input.clientHeight}px`
}

type Snapshot = { rubric: RubricCriterion[]; layout?: RubricLayout }

/** Organisation changes leave the flat grading rubric and every existing ID untouched. */
export function RubricEditor({ value, onChange, layout, onLayoutChange, reservedIds = [], readOnly = false }: {
  value: RubricCriterion[]; onChange: (r: RubricCriterion[]) => void
  layout?: RubricLayout; onLayoutChange?: (layout: RubricLayout | undefined) => void
  /** IDs new criteria must never take: retired ones and those in the last saved rubric, so an unsaved deletion survives remounts. */
  reservedIds?: string[]; readOnly?: boolean
}) {
  const [history, setHistory] = useState<Snapshot[]>([])
  const [announcement, setAnnouncement] = useState('')
  const [query, setQuery] = useState('')
  const [renaming, setRenaming] = useState<string | null>(null)
  const [sectionName, setSectionName] = useState('')
  const [revealRequest, setRevealRequest] = useState<{ criterionId: string }>()
  const editor = useRef<HTMLDivElement>(null)
  const focusTarget = useRef<string | null>(null)
  const focusPending = useCallback(() => {
    if (!focusTarget.current) return
    const input = editor.current?.querySelector<HTMLInputElement | HTMLTextAreaElement>(focusTarget.current)
    if (input?.getClientRects().length) { input.focus(); input.select(); focusTarget.current = null }
  }, [])
  const refitText = useCallback(() => {
    editor.current?.querySelectorAll('textarea').forEach(fitText)
    focusPending()
  }, [focusPending])
  useEffect(focusPending, [value, layout, renaming, focusPending])
  useEffect(() => {
    const resize = () => editor.current?.querySelectorAll('textarea').forEach(fitText)
    window.addEventListener('resize', resize)
    return () => window.removeEventListener('resize', resize)
  }, [])
  const allocated = useRef(new Set<string>())
  const sections = sectionsFor(value, layout)
  const byId = new Map(value.map(c => [c.criterion_id, c]))
  const canOrganise = !!onLayoutChange && !readOnly
  const grouped = sections.length > 1 || sections.some(s => s.title)
  const flip = useRubricFlip(editor, [value, layout])
  const lifted = useRef<{ key: string; rect: DOMRect } | undefined>(undefined)
  const commit = (rubric: RubricCriterion[], nextLayout: RubricLayout | undefined, message: string) => {
    flip(lifted.current)
    setQuery('')
    setHistory(current => [...current.slice(-29), { rubric: value, layout }])
    onChange(rubric)
    onLayoutChange?.(nextLayout)
    setAnnouncement(message)
  }
  const update = (id: string, patch: Partial<RubricCriterion>) => onChange(value.map(c => c.criterion_id === id ? { ...c, ...patch } : c))
  const add = (sectionId: string) => {
    const used = new Set([...value.map(c => c.criterion_id), ...reservedIds, ...allocated.current])
    let n = Math.max(0, ...[...used].map(id => /^R\d+$/.test(id) ? Number(id.slice(1)) : 0)) + 1
    while (used.has(`R${n}`)) n++
    const criterion = blankCriterion(n)
    allocated.current.add(criterion.criterion_id)
    focusTarget.current = `[data-criterion-id="${criterion.criterion_id}"] textarea`
    setRevealRequest({ criterionId: criterion.criterion_id })
    const base = sections.some(s => s.section_id === sectionId) ? sections : [...sections, { section_id: sectionId, title: '', criterion_ids: [] }]
    const next = base.map(s => s.section_id === sectionId ? { ...s, criterion_ids: [...s.criterion_ids, criterion.criterion_id] } : s)
    commit([...value, criterion], onLayoutChange ? next : layout, `Added ${criterion.criterion_id}.`)
  }
  const move = (id: string, sectionId: string, before?: string) => {
    if (id === before) return false
    const next = sections.map(s => ({ ...s, criterion_ids: s.criterion_ids.filter(cid => cid !== id) }))
    const target = next.find(s => s.section_id === sectionId)
    if (!target) return false
    target.criterion_ids.splice(before ? target.criterion_ids.indexOf(before) : target.criterion_ids.length, 0, id)
    if (next.every((s, i) => s.criterion_ids.length === sections[i].criterion_ids.length && s.criterion_ids.every((cid, j) => cid === sections[i].criterion_ids[j]))) return false
    commit(value, next, `Moved ${id} to ${target.title || 'Unsectioned'}.`)
    return true
  }
  const moveSection = (id: string, index: number) => {
    if (sections.findIndex(s => s.section_id === id) === index) return false
    const next = sections.filter(s => s.section_id !== id)
    const section = sections.find(s => s.section_id === id)
    if (!section) return false
    next.splice(index, 0, section)
    commit(value, next, `Moved section ${section.title || 'Unsectioned'}.`)
    return true
  }
  const removeSection = (section: RubricSection) => {
    const next = sections.filter(s => s.section_id !== section.section_id)
    if (section.criterion_ids.length) {
      const unsectioned = next.find(s => s.section_id === 'unsectioned')
      if (unsectioned) unsectioned.criterion_ids = [...unsectioned.criterion_ids, ...section.criterion_ids]
      else next.push({ section_id: 'unsectioned', title: '', criterion_ids: section.criterion_ids })
    }
    commit(value, next, 'Section removed. Its criteria were kept in Unsectioned.')
  }
  const drop = (item: RubricDragItem, destination: RubricDropTarget, from: DOMRect) => {
    lifted.current = { key: `${item.kind}:${item.id}`, rect: from }
    try {
      if (item.kind === 'section') {
        const remaining = sections.filter(s => s.section_id !== item.id)
        const index = remaining.findIndex(s => s.section_id === destination.sectionId)
        return index >= 0 && moveSection(item.id, index + (destination.edge === 'after' ? 1 : 0))
      }
      const ids = sections.find(s => s.section_id === destination.sectionId)?.criterion_ids.filter(id => id !== item.id) ?? []
      const before = destination.criterionId && destination.edge === 'after'
        ? ids[ids.indexOf(destination.criterionId) + 1] : destination.criterionId
      return move(item.id, destination.sectionId, before)
    } finally { lifted.current = undefined }
  }
  const startDrag = useRubricDrag(editor, drop)
  const finishRename = () => {
    if (!renaming) return
    onLayoutChange?.(sections.map(s => s.section_id === renaming ? { ...s, title: sectionName.trim() || s.title } : s))
    setRenaming(null)
  }
  return <div ref={editor} className="rubric-editor">
    <RubricOutline bulkToggles={false} onVisibilityChange={refitText} revealRequest={revealRequest} searchQuery={query} onSearchChange={setQuery} sections={sections} text={id => `${byId.get(id)?.criterion ?? ''} ${byId.get(id)?.evidence ?? ''}`} editable={!readOnly}
      actions={!readOnly && <>
        <button type="button" className="rubric-undo" aria-label="Undo" title="Undo last structural change" disabled={!history.length} onClick={() => {
          const previous = history[history.length - 1]
          flip()
          const current = new Map(value.map(c => [c.criterion_id, c]))
          onChange(previous.rubric.map(c => current.get(c.criterion_id) ?? c))
          onLayoutChange?.(previous.layout?.map(s => ({ ...s, title: layout?.find(item => item.section_id === s.section_id)?.title ?? s.title })))
          setHistory(current => current.slice(0, -1)); setAnnouncement('Undid the last structural change.')
        }}><Icon name="refresh" size={15} style={{ transform: 'scaleX(-1)' }} /> Undo</button>
      </>}
      header={canOrganise && grouped ? (section, index, controls) => <>
        <span className="rubric-grip" data-rubric-grip onPointerDown={e => startDrag(e, { kind: 'section', id: section.section_id })} title="Drag section" aria-hidden="true"><Icon name="grip" size={16} /></span>
        {renaming === section.section_id ? <input data-section-name={section.section_id} className="rubric-section-name" aria-label="Section name" maxLength={120} value={sectionName} onChange={e => setSectionName(e.target.value)} onBlur={finishRename} onKeyDown={e => {
          if (e.key === 'Enter') { e.preventDefault(); finishRename() }
          if (e.key === 'Escape') { e.preventDefault(); setRenaming(null) }
        }} /> : <button type="button" className="rubric-section-title" {...controls}><span className="rubric-chevron"><Icon name="chevron" size={15} /></span>{section.title || 'Unsectioned'}</button>}
        <span className="muted small rubric-section-count">{section.criterion_ids.length} {section.criterion_ids.length === 1 ? 'criterion' : 'criteria'}</span>
        <div className="rubric-section-actions" role="group" aria-label={`Actions for section ${section.title || 'Unsectioned'}`}>
          {section.section_id !== 'unsectioned' && <button type="button" className="ghost sm" aria-label={`Rename section ${section.title}`} title="Rename section" onClick={() => {
            focusTarget.current = `[data-section-name="${section.section_id}"]`
            setSectionName(section.title); setRenaming(section.section_id)
          }}><Icon name="pencil" size={14} /></button>}
          <button type="button" className="ghost sm" aria-label={`Move section ${section.title || 'Unsectioned'} up`} disabled={!index} onClick={() => { moveSection(section.section_id, index - 1) }}><Icon name="arrow" size={14} style={{ transform: 'rotate(-90deg)' }} /></button>
          <button type="button" className="ghost sm" aria-label={`Move section ${section.title || 'Unsectioned'} down`} disabled={index === sections.length - 1} onClick={() => { moveSection(section.section_id, index + 1) }}><Icon name="arrow" size={14} style={{ transform: 'rotate(90deg)' }} /></button>
          {section.section_id !== 'unsectioned' && <button type="button" className="ghost sm rubric-delete" aria-label="Remove section" title="Remove section" onClick={() => { removeSection(section) }}><Icon name="trash" size={14} /></button>}
        </div>
      </> : undefined}>
      {(section, ids) => <>
        {ids.map((id, index) => {
          const c = byId.get(id)!
          return <div className="rubric-edit rubric-card" key={id} data-criterion-id={id}>
            <div className="head">
              {canOrganise && <span className="rubric-grip" data-rubric-grip onPointerDown={e => startDrag(e, { kind: 'criterion', id })} title={`Drag ${id}`} aria-hidden="true"><Icon name="grip" size={16} /></span>}
              <span className="id" title="Stable criterion ID">{id}</span>
              <Picker label="Role" value={c.role} disabled={readOnly} onChange={role => update(id, { role: role as RubricCriterion['role'] })}
                options={['required', 'bonus', 'penalty'].map(role => ({ value: role, label: role }))} />
              {canOrganise && <div className="rubric-card-actions" role="group" aria-label={`Actions for ${id}`}>
                <button type="button" className="ghost sm rubric-direction" aria-label={`Move ${id} up`} title="Move up" disabled={index === 0} onClick={() => move(id, section.section_id, ids[index - 1])}><Icon name="arrow" size={14} style={{ transform: 'rotate(-90deg)' }} /></button>
                <button type="button" className="ghost sm rubric-direction" aria-label={`Move ${id} down`} title="Move down" disabled={index === ids.length - 1} onClick={() => move(id, section.section_id, section.criterion_ids[section.criterion_ids.indexOf(ids[index + 1]) + 1])}><Icon name="arrow" size={14} style={{ transform: 'rotate(90deg)' }} /></button>
              </div>}
              {!readOnly && value.length > MIN_CRITERIA && <button type="button" className="ghost sm rubric-delete rubric-remove" aria-label="Remove" title={`Remove ${id}`} onClick={() => {
                allocated.current.add(id)
                commit(value.filter(item => item.criterion_id !== id), onLayoutChange ? sections.map(s => ({ ...s, criterion_ids: s.criterion_ids.filter(cid => cid !== id) })) : layout, `Removed ${id}. Undo to restore it.`)
              }}><Icon name="trash" size={14} /></button>}
            </div>
            <label className="field rubric-criterion-field"><span className="visually-hidden">Criterion</span><textarea rows={1} ref={fitText} onInput={e => fitText(e.currentTarget)} readOnly={readOnly} value={c.criterion} onChange={e => update(id, { criterion: e.target.value })} placeholder="Describe one result that can pass or fail." /></label>
            <label className="field rubric-evidence-field"><span className="visually-hidden">How would you check it?</span><textarea rows={1} ref={fitText} onInput={e => fitText(e.currentTarget)} className="rubric-evidence" readOnly={readOnly} value={c.evidence ?? ''} onChange={e => update(id, { evidence: e.target.value })} placeholder="How would you check this? Say what you would inspect or measure." /></label>

          </div>
        })}
        {!readOnly && section.section_id !== 'unsectioned' && <button className="sm rubric-add" type="button" disabled={value.length >= 100} onClick={() => add(section.section_id)} aria-label={`Add criterion to ${section.title}`}><Icon name="plus" size={14} /> Add criterion</button>}
      </>}
    </RubricOutline>
    {!readOnly && <div className="rubric-footer">
      <button type="button" className="rubric-add-loose" disabled={value.length >= 100} onClick={() => add('unsectioned')}
        aria-label={grouped ? 'Add criterion outside a section' : 'Add criterion'}><Icon name="plus" size={15} /> {grouped ? 'Add criterion outside a section' : 'Add criterion'}</button>
      {onLayoutChange && <button type="button" className="rubric-new-section" disabled={sections.filter(s => s.section_id !== 'unsectioned').length >= 30} onClick={() => {
        const id = `section-${crypto.randomUUID()}`
        focusTarget.current = `[data-section-name="${id}"]`
        setRenaming(id); setSectionName('New section')
        commit(value, [...sections, { section_id: id, title: 'New section', criterion_ids: [] }], 'Section added. Enter its name.')
      }}><Icon name="plus" size={15} /> Add section</button>}
    </div>}
    <span className="visually-hidden" role="status" aria-live="polite">{announcement}</span>
  </div>
}

export function cleanRubric(rubric: RubricCriterion[]): RubricCriterion[] {
  return rubric.map(c => Object.fromEntries(Object.entries(c).filter(([k, v]) => v !== undefined && (k === 'criterion' || v !== ''))) as unknown as RubricCriterion)
}
